import { runInAction } from "mobx"
import { toast } from "sonner"

import type {
  AiAgentTask,
  AiAgentTaskSettings,
  AiChatMessageAttachment,
  CPrincipal,
  CUserPrincipalDetails,
  ProfileIdentifier,
  ProjectKey
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { MouseEvent } from "react"

export function isNullish(value: unknown): value is null | undefined {
  return value === null || value === undefined
}

export function isNull(value: unknown): value is null {
  return value === null
}

export function isNotNull(value: unknown): value is NonNullable<typeof value> {
  return !isNull(value)
}

export function mapNotNull<T, U>(items: T[], mapper: (item: T) => U | null): U[] {
  return items.map(mapper).filter(isNotNull) as U[]
}

export function filterNotNull<T>(items: (T | null)[]): T[] {
  return (items || []).filter(it => it !== null) as T[]
}

export function firstNotNullOf<T>(...items: (T | null)[]): T | null {
  return items.find(isNotNull) || null
}

export function lastOrNull<T>(items: T[], predicate: (_: T) => boolean): T | null {
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i]
    if (predicate(item)) {
      return item
    }
  }

  return null
}

export function isNumber(value: unknown): value is number {
  return typeof value === "number" && !isNaN(value)
}

export function compareTo(lhs: number, rhs: number): number {
  if (lhs < rhs) {
    return -1
  }

  if (lhs > rhs) {
    return 1
  }

  return 0
}

export function toProfileUsername(username: string): ProfileIdentifier.Username {
  return { className: "Username", username }
}

// Can be used to compare string representations of integer numbers (without leading zeroes or other extra symbols)
export const compareStringAsInteger = (a: string, b: string): number => {
  const aSign = (a.length > 0 && a[0] === "-") ? -1 : 1
  const bSign = (b.length > 0 && b[0] === "-") ? -1 : 1
  if (aSign != bSign) {
    return aSign - bSign
  }
  if (a.length !== b.length) {
    return (a.length - b.length) * aSign
  }
  if (a === b) {
    return 0
  }
  return (a < b ? -1 : 1) * aSign
}

export function recordLength<K extends string | number | symbol, V>(record: Record<K, V>) {
  return Object.keys(record).length
}

export function recordToMap<K extends string | number | symbol, V>(record: Record<K, V>): Map<K, V> {
  const map = new Map<K, V>()
  for (const key in record) {
    if (Object.prototype.hasOwnProperty.call(record, key)) {
      map.set(key as K, record[key])
    }
  }
  return map
}

export function openLinkInNewWindow(href: string, noreferrer: boolean = true) {
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/open
  window.open(href, "_blank", noreferrer ? "noreferrer" : undefined)
}

export function parseTaskSettings(task: AiAgentTask): AiAgentTaskSettings {
  return JSON.parse(task.settings || "{}") as AiAgentTaskSettings
}

export async function copyToClipboard(text: string | null, silent = false) {
  if (text && document.hasFocus()) {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text).then(() => {
          if (!silent) toast.success("Copied to clipboard", { testId: "copy-branch-success" })
        })
        return
      }

      const textarea = document.createElement("textarea")
      textarea.value = text
      textarea.style.position = "fixed"
      textarea.style.opacity = "0"
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()

      // eslint-disable-next-line @typescript-eslint/no-deprecated
      const successful = document.execCommand("copy")
      document.body.removeChild(textarea)

      if (!successful) {
        toast.error("Failed to copy to clipboard")
      }
    } catch {
      toast.error("Failed to copy to clipboard")
    }
  }
}

export const preventTextSelection = (e: MouseEvent) => {
  // Prevent text selection on repeated clicks
  if (e.detail > 1) {
    e.preventDefault()
  }
}

export function splitPath(fullPath: string): { filename: string, parentPath: string | null } {
  const parts = fullPath.split("/")
  const filename = parts.pop() || ""
  const parentPath = parts.join("/") || null
  return { filename, parentPath: parentPath && parentPath.startsWith("/") ? parentPath : null }
}

export function looksLikeBinary(str: string): boolean {
  let nonPrintable = 0
  const substring = Math.min(str.length, 1000)

  for (let i = 0; i < substring; i++) {
    const code = str.charCodeAt(i)
    if (code <= 8 || (code >= 14 && code <= 31) || code === 65533) {
      nonPrintable++
      if ((nonPrintable / substring) > 0.01) return true
    }
  }

  return false
}

const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp", ".ico"]

export function isImageFile(filename: string | undefined): boolean {
  if (!filename) return false
  const extension = filename.toLocaleLowerCase().substring(filename.lastIndexOf("."))
  return imageExtensions.includes(extension)
}

export function renameFileIfNeeded(file: File, filenames: string[]): File {
  const originalName = file.name
  let name = originalName
  let counter = 1

  while (filenames.includes(name)) {
    const extIndex = originalName.lastIndexOf(".")
    const base = extIndex === -1 ? originalName : originalName.slice(0, extIndex)
    const ext = extIndex === -1 ? "" : originalName.slice(extIndex)

    name = `${base} (${counter})${ext}`
    counter++
  }

  return name === file.name ? file : new File([file], name, { type: file.type })
}

export function maybeSetTimeout(callback: () => void, delay: number): void {
  if (delay > -1) {
    setTimeout(callback, delay)
  } else {
    callback()
  }
}

export function isUserPrincipal(principal: CPrincipal | undefined): principal is CPrincipal & {
  details: CUserPrincipalDetails
} {
  return principal?.details?.className === "CUserPrincipalDetails"
}

export async function successfulAttachments(attachments: Promise<AiChatMessageAttachment>[]): Promise<AiChatMessageAttachment[]> {
  return await Promise.allSettled(attachments)
    .then(results => {
      return results
        .map(result => result.status === "fulfilled" ? result.value : null)
        .filter(result => result !== null) as AiChatMessageAttachment[]
    })
}

export function normalizeProjectKey(projectKey: ProjectKey) {
  return projectKey.key.toLocaleLowerCase()
}

export async function optimisticAction<T>(
  applyOptimistic: () => void,
  perform: () => Promise<T>,
  rollback: () => void
): Promise<T> {
  runInAction(() => applyOptimistic())
  try {
    return await perform()
  } catch (err) {
    runInAction(() => rollback())
    throw err
  }
}
